/**
 * SeoHead — canonical + Open Graph POR ROTA.
 *
 * Achado da auditoria de 03/09/2026: as 21 páginas geradas pelo SSG carregavam
 * TODAS o mesmo `<link rel="canonical" href="https://macacodaspromo.com.br/">`,
 * hardcoded no index.html. Para o Google isso declara que /cupons/amazon/casa/
 * é uma cópia da home — as páginas de cupom não competem por nada, e o trabalho
 * de gerar 20 rotas estáticas era desperdiçado. (As páginas /cupons saíram do ar
 * em 2026-10-23.)
 *
 * O canonical e o og:url saíram do index.html e passaram a ser injetados aqui,
 * por rota, via o <Head> do vite-react-ssg (funciona no build estático e na
 * navegação client-side).
 *
 * Convenção de URL: SEMPRE com barra final, exceto a raiz. A Cloudflare Pages
 * faz pretty-URL de diretório e responde 308 para a versão sem barra — declarar a
 * URL sem barra no canonical apontaria para um redirect.
 */
import { Head } from "vite-react-ssg";

const ORIGEM = "https://macacodaspromo.com.br";

export interface SeoHeadProps {
  /** Caminho da rota, começando com "/" (ex.: "/camping/"). */
  path: string;
  title?: string;
  description?: string;
  /** Páginas sem valor de busca (404) devem sair do índice. */
  noindex?: boolean;
  /**
   * Preview próprio da página (og:image/twitter:image), URL absoluta. Sem isso a
   * página herda o og-image.jpg da home do index.html — ruim para landing de
   * nicho compartilhada no WhatsApp, onde o preview É o anúncio.
   */
  image?: string;
  imageAlt?: string;
  /** Cor da barra do navegador mobile quando a página não usa o vermelho da marca. */
  themeColor?: string;
  /**
   * Classe no <html> gravada no HTML estático — permite trocar o fundo global
   * (vermelho, fixo em html::before) ANTES do JS rodar, sem flash. Ver
   * `html.page-camping` no index.css.
   */
  htmlClass?: string;
}

/** Normaliza para a forma canônica: origem + caminho com barra final. */
export function canonicalUrl(path: string): string {
  if (!path || path === "/") return `${ORIGEM}/`;
  const limpo = `/${path.replace(/^\/+/, "").replace(/\/+$/, "")}/`;
  return `${ORIGEM}${limpo}`;
}

const TITULO_PADRAO = "Macaco das Promos — Bugs de Preço no seu WhatsApp e Telegram, grátis 🐒";
const DESC_PADRAO =
  "+92 mil pessoas economizam todo dia com a gente. Promos da Amazon, Magalu, Shopee e Mercado Livre direto no seu celular. 100% grátis.";

export function SeoHead({
  path,
  title,
  description,
  noindex,
  image,
  imageAlt,
  themeColor,
  htmlClass,
}: SeoHeadProps) {
  // SÓ no build estático. O <Head> do vite-react-ssg depende de um provider que
  // apenas o entry do ViteReactSSG monta (src/ssg.tsx); o entry do cliente
  // (src/main.tsx → <App/>) não tem esse provider, então renderizar Head no
  // navegador lança "Cannot read properties of undefined (reading 'add')" dentro
  // do HelmetDispatcher — o que derrubou a hidratação e deixou o site EM BRANCO
  // em produção em 03/09/2026.
  //
  // Nada se perde ao pular no cliente: as tags já foram gravadas no HTML durante
  // o SSG, que é onde buscador e crawler leem. Navegação client-side não precisa
  // reescrever canonical.
  if (!import.meta.env.SSR) return null;

  const url = canonicalUrl(path);
  const t = title ?? TITULO_PADRAO;
  const d = description ?? DESC_PADRAO;
  return (
    <Head>
      <link rel="canonical" href={url} />
      <meta property="og:url" content={url} />
      <title>{t}</title>
      <meta property="og:title" content={t} />
      <meta name="twitter:title" content={t} />
      <meta name="description" content={d} />
      <meta property="og:description" content={d} />
      <meta name="twitter:description" content={d} />
      {noindex ? <meta name="robots" content="noindex, follow" /> : null}
      {htmlClass ? <html className={htmlClass} /> : null}
      {themeColor ? <meta name="theme-color" content={themeColor} /> : null}
      {image ? <meta property="og:image" content={image} /> : null}
      {image ? <meta name="twitter:image" content={image} /> : null}
      {image && imageAlt ? <meta property="og:image:alt" content={imageAlt} /> : null}
    </Head>
  );
}
